import { useState, useRef, useCallback } from 'react';
import { Message } from '../types';
import { useLocalStorage } from './useLocalStorage';
import { useScrollToBottom } from './useScrollToBottom';
import { TIMING, STORAGE_KEYS, LIMITS } from '../constants';
import { COMMANDS } from '../constants/commands';
import { sendChatMessage } from '../services/chatService';
import {
  createUserMessage,
  createAssistantMessage,
  createErrorMessage,
  createThinkingMessage,
} from '../utils/messages';
import { focusInput } from '../utils/focus';
import { validateEmail } from '../utils/validation';
import { getErrorMessage } from '../utils/errors';

type InputMode = 'normal' | 'email-early-access' | 'email-get-started';

interface UseTerminalProps {
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function useTerminal({ messagesEndRef, inputRef }: UseTerminalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatMode, setChatMode] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const [inputMode, setInputMode] = useState<InputMode>('normal');

  const [isInitialized, setIsInitialized] = useLocalStorage<boolean>(
    STORAGE_KEYS.INITIALIZED,
    false
  );

  const [isInitializing, setIsInitializing] = useState(false);
  const hasRunInitialization = useRef(false);

  // Auto-scroll to bottom
  useScrollToBottom(messagesEndRef, [messages, isTyping]);

  const runInitialization = useCallback(async () => {
    if (hasRunInitialization.current) return;
    hasRunInitialization.current = true;
    setIsInitializing(true);

    const initMessages = [
      'MIFTA Terminal v1.0.2',
      'Initializing automation environment...',
      'Loading system modules................ [OK]',
      'Starting terminal emulator............ [OK]',
      'Configuring workflow engine........... [OK]',
      'Establishing AI connection............ [OK]',
      'Environment ready.',
      '',
      'Welcome to MIFTA - Make It Fast Through Automation',
      "Type 'help' to see available commands.",
    ];

    for (let i = 0; i < initMessages.length; i++) {
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          i < 2 ? TIMING.INIT_DELAY : TIMING.INIT_MESSAGE_DELAY
        )
      );
      setMessages((prev) => [
        ...prev,
        createAssistantMessage(initMessages[i], { id: crypto.randomUUID() }),
      ]);
    }

    await new Promise((resolve) =>
      setTimeout(resolve, TIMING.INIT_COMPLETE_DELAY)
    );

    setMessages([]);
    setIsInitialized(true);
    setIsInitializing(false);
    focusInput(inputRef);
  }, [setIsInitialized, inputRef]);

  // Helper: Show thinking state with animation
  const showThinkingState = useCallback(async (thinkingContent: string) => {
    setIsTyping(true);

    await new Promise((resolve) =>
      setTimeout(
        resolve,
        TIMING.THINKING_MIN_DELAY +
          Math.random() *
            (TIMING.THINKING_MAX_DELAY - TIMING.THINKING_MIN_DELAY)
      )
    );

    setMessages((prev) => [
      ...prev,
      createThinkingMessage(thinkingContent, { id: crypto.randomUUID() }),
    ]);

    await new Promise((resolve) =>
      setTimeout(resolve, TIMING.THINKING_ANIMATION_DURATION)
    );

    setIsTyping(false);
  }, []);

  // Helper: Handle email input mode
  const handleEmailInput = useCallback(
    async (email: string, mode: InputMode) => {
      const validation = validateEmail(email);

      if (!validation.valid) {
        setMessages((prev) => [
          ...prev,
          createErrorMessage(
            validation.error || '⚠️ Please enter a valid email address.',
            { id: crypto.randomUUID() }
          ),
        ]);
        focusInput(inputRef);
        return;
      }

      setMessages((prev) => [
        ...prev,
        createUserMessage(email, false, { id: crypto.randomUUID() }),
      ]);

      await showThinkingState(
        mode === 'email-early-access'
          ? 'Submitting your request...'
          : 'Preparing your guide...'
      );

      const successContent =
        mode === 'email-early-access'
          ? `✅ Thank you! Your request has been received.\n\nWe'll notify you at ${email} when early access is available.\nGet ready to automate everything!\n\nIn the meantime, feel free to explore the terminal with 'help' command.`
          : `✅ Perfect! Check your email at ${email} for the complete getting started guide.\n\nWe've sent you:\n• Installation instructions\n• Quick start tutorial\n• Best practices guide\n• Sample automation workflows\n\nTry typing 'help' to explore the terminal now!`;

      setMessages((prev) => [
        ...prev,
        createAssistantMessage(successContent, { id: crypto.randomUUID() }),
      ]);
      setInputMode('normal');
      focusInput(inputRef);
    },
    [inputRef, showThinkingState]
  );

  // Helper: Handle chat mode exit
  const handleChatExit = useCallback(() => {
    setChatMode(false);
    setMessages((prev) => [
      ...prev,
      createAssistantMessage("Exited chat mode. You're back to command mode.", {
        id: crypto.randomUUID(),
      }),
    ]);
    focusInput(inputRef);
  }, [inputRef]);

  // Helper: Handle special commands (clear, reset-init)
  const handleSpecialCommands = useCallback(
    (trimmedCommand: string, isChatMode: boolean) => {
      if (!isChatMode && trimmedCommand === 'clear') {
        setTimeout(() => {
          setMessages([]);
          focusInput(inputRef);
        }, TIMING.CLEAR_DELAY);
        return true;
      }

      if (!isChatMode && trimmedCommand === 'reset-init') {
        setIsInitialized(false);
        hasRunInitialization.current = false;
        setMessages([]);
        setRequestCount(0);
        setChatMode(false);

        setMessages((prev) => [
          ...prev,
          createAssistantMessage(
            '🔄 Initialization reset successfully.\n\nThe terminal will run the boot sequence on your next command.\nAll chat request counters have been cleared.',
            { id: crypto.randomUUID() }
          ),
        ]);
        focusInput(inputRef);
        return true;
      }

      return false;
    },
    [inputRef, setIsInitialized]
  );

  // Helper: Check request limit for chat mode
  const checkRequestLimit = useCallback(
    (isChatMode: boolean): boolean => {
      if (isChatMode && requestCount >= LIMITS.MAX_CHAT_REQUESTS) {
        setMessages((prev) => [
          ...prev,
          createErrorMessage(
            `⚠️ You've reached the maximum number of chat requests (${LIMITS.MAX_CHAT_REQUESTS}).\n\nThis is a demo environment with limited resources. Please try again later or request early access to MIFTA for unlimited usage.\n\nType 'exit' to return to command mode.`,
            { id: crypto.randomUUID() }
          ),
        ]);
        focusInput(inputRef);
        return true;
      }
      return false;
    },
    [requestCount, inputRef]
  );

  // Helper: Handle chat mode execution
  const handleChatModeExecution = useCallback(async (command: string) => {
    setRequestCount((prev) => prev + 1);

    try {
      const aiResponse = await sendChatMessage(command);
      setMessages((prev) => [
        ...prev,
        createAssistantMessage(aiResponse, { id: crypto.randomUUID() }),
      ]);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setMessages((prev) => [
        ...prev,
        createErrorMessage(
          `⚠️ AI chat failed.\n\nReason: ${errorMessage}\n\nTry again later or switch to another model.`,
          { id: crypto.randomUUID() }
        ),
      ]);
    }
  }, []);

  // Helper: Handle command mode execution
  const handleCommandModeExecution = useCallback(
    (trimmedCommand: string, originalCommand: string) => {
      const commandExists = COMMANDS[trimmedCommand as keyof typeof COMMANDS];
      const isError = !commandExists;
      const response = commandExists?.response;

      if (isError) {
        setMessages((prev) => [
          ...prev,
          createAssistantMessage(
            `Command '${originalCommand}' not recognized. Type 'help' for list.`,
            {
              id: crypto.randomUUID(),
              isError: true,
            }
          ),
        ]);
      } else if (response) {
        if (typeof response === 'string') {
          setMessages((prev) => [
            ...prev,
            createAssistantMessage(response, {
              id: crypto.randomUUID(),
            }),
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              ...response,
              id: crypto.randomUUID(),
              timestamp: new Date(),
            },
          ]);
        }
      }
    },
    []
  );

  const handleCommand = useCallback(
    async (command: string, isChatMode: boolean = false) => {
      if (!isInitialized && !isInitializing) {
        await runInitialization();
      }

      const trimmedCommand = command.trim().toLowerCase();

      // Handle email input mode
      if (inputMode !== 'normal') {
        await handleEmailInput(command.trim(), inputMode);
        return;
      }

      // Handle exit from chat mode
      if (
        isChatMode &&
        (trimmedCommand === 'exit' || trimmedCommand === 'quit')
      ) {
        handleChatExit();
        return;
      }

      // Add user message
      setMessages((prev) => [
        ...prev,
        createUserMessage(command, isChatMode, { id: crypto.randomUUID() }),
      ]);

      // Handle special commands (clear, reset-init)
      if (handleSpecialCommands(trimmedCommand, isChatMode)) {
        return;
      }

      // Check request limit for chat mode
      if (checkRequestLimit(isChatMode)) {
        return;
      }

      // Get thinking content - fix TypeScript error by checking if thinking exists
      const commandDef = COMMANDS[trimmedCommand as keyof typeof COMMANDS];
      let thinkingContent = 'Processing your request';
      if (isChatMode) {
        thinkingContent = 'thinking';
      } else if (commandDef && 'thinking' in commandDef) {
        thinkingContent = commandDef.thinking;
      }

      await showThinkingState(thinkingContent);

      // Handle chat mode or command mode
      if (isChatMode) {
        await handleChatModeExecution(command);
      } else {
        handleCommandModeExecution(trimmedCommand, command);
      }

      // Note: Focus will be handled by Terminal component when animation completes
    },
    [
      isInitialized,
      isInitializing,
      inputMode,
      runInitialization,
      handleEmailInput,
      handleChatExit,
      handleSpecialCommands,
      checkRequestLimit,
      showThinkingState,
      handleChatModeExecution,
      handleCommandModeExecution,
    ]
  );

  const toggleChatMode = useCallback(async () => {
    if (!isInitialized && !isInitializing) {
      await runInitialization();
    }

    setChatMode(true);
    setMessages((prev) => [
      ...prev,
      createAssistantMessage(
        `💬 Entered chat mode! You can now chat freely with MIFTA AI. Ask me anything about automation, workflows, or development.\n\nType 'exit' or 'quit' to return to command mode.`,
        { id: crypto.randomUUID() }
      ),
    ]);
    focusInput(inputRef);
  }, [isInitialized, isInitializing, runInitialization, inputRef]);

  const handleCTAClick = useCallback(
    async (type: 'early-access' | 'get-started') => {
      if (!isInitialized && !isInitializing) {
        await runInitialization();
      }

      const message =
        type === 'early-access'
          ? '✨ Welcome! To get early access to MIFTA,\n\nPlease enter your email address below:'
          : "🚀 Let's get you started with MIFTA!\n\nPlease enter your email address to receive the getting started guide:";

      setMessages((prev) => [
        ...prev,
        createAssistantMessage(message, { id: crypto.randomUUID() }),
        createThinkingMessage('Waiting for your email (Press ESC to cancel)', {
          id: crypto.randomUUID(),
        }),
      ]);

      setInputMode(
        type === 'early-access' ? 'email-early-access' : 'email-get-started'
      );
      focusInput(inputRef);
    },
    [isInitialized, isInitializing, runInitialization, inputRef]
  );

  const handleExitEmailMode = useCallback(() => {
    setMessages((prev) => {
      const filtered = prev.filter((msg) => !msg.isThinking);
      return [
        ...filtered,
        createAssistantMessage(
          '❌ Email input cancelled. Returned to command mode.',
          {
            id: crypto.randomUUID(),
          }
        ),
      ];
    });
    setInputMode('normal');
    focusInput(inputRef);
  }, [inputRef]);

  const handleClear = useCallback(() => {
    setMessages([]);
    setInputMode('normal');
    setChatMode(false);
    focusInput(inputRef);
  }, [inputRef]);

  return {
    messages,
    isTyping,
    chatMode,
    inputMode,
    isInitialized,
    isInitializing,
    handleCommand,
    toggleChatMode,
    handleCTAClick,
    handleExitEmailMode,
    handleClear,
  };
}
